import { Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { ReservationService } from "./services/ReservationService";
import { NotificationsProvider } from "./context/NotificationsContext";
import { useVersionCheck } from "./hooks/useVersionCheck";
import { UpdateBanner } from "./components/UpdateBanner";

import "./App.css";
import Home from "./pages/Home";
import Login from "./pages/Login";
import Confirm from "./pages/ConfirmDay";
import MyReservations from "./pages/MyReservations";
import EditProfile from "./pages/EditProfile";

import AdminHome from "./pages/admin/Home";
import Schools from "./pages/admin/Schools";
import Users from "./pages/admin/Users";
import Navbar from "./components/admin/Navbar";
import SchoolEdit from "./pages/admin/SchoolEdit";
import UserEdit from "./pages/admin/UserEdit";
import AvailableReservations from "./pages/AvailableReservations";

import { SelectedDateProvider } from "./context/SelectedDateContext";
import ConfirmReservation from "./pages/ConfirmReservation";
import AuthMiddleware from "./middlewares/AuthMiddleware";
import TermsMiddleware from "./middlewares/TermsMiddleware";
import TermosDeUso from "./pages/TermosDeUso";
import UnderDevelopment from "./pages/UnderDevelopment";
import ReservationHistory from "./pages/ReservationHistory";
import Checkin from "./pages/Checkin";
import Rules from "./pages/admin/Rules";
import UpdateTerms from "./pages/admin/UpdateTerms";
import BlockUser from "./pages/admin/BlockUser";
import Reports from "./pages/admin/Reports";
import ReportFechamentoSintetico from "./pages/admin/reports/FechamentoSintetico";
import { ReportProvider } from "./context/ReportContext";
import ReportRelatorioAnalitico from "./pages/admin/reports/RelatorioAnalitico";
import ReportCancelamento from "./pages/admin/reports/RelatorioCancelamento";
import ReportEscalas from "./pages/admin/reports/RelatorioEscalas";
import ReportFechamentoDepPessoal from "./pages/admin/reports/FechamentoDepPessoal";
import ConfirmCancelReservation from "./pages/ConfirmCancelReservation";
import Reservations from "./pages/admin/Reservations";
import ReservationEdit from "./pages/admin/ReservationEdit";
import ActivityLogs from "./pages/admin/ActivityLogs";
import SystemLogs from "./pages/admin/SystemLogs";
import Roles from "./pages/admin/Roles";
import RoleEdit from "./pages/admin/RoleEdit";

function AdminMiddleware({ children }) {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");
  const roleName = localStorage.getItem("roleName");
  const isAdminUser =
    localStorage.getItem("roleType") === "ADMIN" ||
    localStorage.getItem("isMaster") === "true" ||
    roleName === "Comando";

  if (isAdminRoute && !isAdminUser) {
    window.location.href = "/"; // Forces a full page reload
    return null; // Prevents rendering anything while redirecting
  }

  return children;
}

function App() {
  const roleName = localStorage.getItem("roleName");
  const isAdminUser =
    localStorage.getItem("roleType") === "ADMIN" ||
    localStorage.getItem("isMaster") === "true" ||
    roleName === "Comando";

  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");

  // Version check for cache busting
  const { needsUpdate, reloadApp } = useVersionCheck();

  useEffect(() => {
    // Only check for canceled reservations if authenticated and not on admin route
    const authToken = localStorage.getItem("authToken");
    const isLoginPage = location.pathname === "/login";

    if (!isAdminRoute && authToken && !isLoginPage) {
      const reservationService = new ReservationService();
      const checkCanceledReservations = async () => {
        try {
          const canceledReservations =
            await reservationService.getCanceledReservations();
          if (canceledReservations && canceledReservations.length > 0) {
            console.log("Canceled reservations found:", canceledReservations);
            // Add a notification for each canceled reservation
            // TODO: Implement notification system
          }
        } catch (error) {
          console.error("Error checking canceled reservations:", error);
        }
      };

      // Check immediately
      checkCanceledReservations();

      // Set up interval to check every 10 minutes
      const intervalId = setInterval(checkCanceledReservations, 10 * 60 * 1000);

      // Clean up interval on component unmount
      return () => clearInterval(intervalId);
    }
  }, [isAdminRoute, location.pathname]);

  return (
    <SelectedDateProvider>
      <AuthMiddleware>
        <TermsMiddleware>
          <AdminMiddleware>
            <ReportProvider>
              <NotificationsProvider>
                <div>
                  {/* Show update banner when new version is available */}
                  {needsUpdate && <UpdateBanner onReload={reloadApp} />}

                  <div>{isAdminUser && <Navbar />}</div>

                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/confirm" element={<Confirm />} />
                    <Route path="/tbi" element={<UnderDevelopment />} />
                    <Route
                      path="/my-reservations"
                      element={<MyReservations />}
                    />
                    <Route
                      path="/reservation-history"
                      element={<ReservationHistory />}
                    />
                    <Route path="/checkin" element={<Checkin />} />
                    <Route path="/edit-profile" element={<EditProfile />} />
                    <Route
                      path="/available-reservations"
                      element={<AvailableReservations />}
                    />
                    <Route
                      path="/confirm-reservation"
                      element={<ConfirmReservation />}
                    />
                    <Route
                      path="/confirm-cancel-reservation"
                      element={<ConfirmCancelReservation />}
                    />

                    {/* Admin routes */}
                    <Route path="/admin" element={<AdminHome />} />
                    <Route path="/admin/schools" element={<Schools />} />
                    <Route path="/admin/schools/new" element={<SchoolEdit />} />
                    <Route
                      path="/admin/schools/:schoolId"
                      element={<SchoolEdit />}
                    />
                    <Route path="/admin/users" element={<Users />} />
                    <Route path="/admin/users/new" element={<UserEdit />} />
                    <Route path="/admin/users/:userId" element={<UserEdit />} />
                    <Route path="/admin/rules" element={<Rules />} />
                    <Route path="/admin/roles" element={<Roles />} />
                    <Route path="/admin/roles/new" element={<RoleEdit />} />
                    <Route path="/admin/roles/:roleId" element={<RoleEdit />} />
                    <Route
                      path="/admin/update-terms"
                      element={<UpdateTerms />}
                    />
                    <Route path="/admin/reports" element={<Reports />} />
                    <Route path="/admin/block-user" element={<BlockUser />} />
                    <Route
                      path="/admin/reservations"
                      element={<Reservations />}
                    />
                    <Route
                      path="/admin/activity-logs"
                      element={<ActivityLogs />}
                    />
                    <Route
                      path="/admin/system-logs"
                      element={<SystemLogs />}
                    />
                    <Route
                      path="/admin/reservations/new"
                      element={<ReservationEdit />}
                    />
                    <Route
                      path="/admin/reservations/:reservationId"
                      element={<ReservationEdit />}
                    />

                    {/* Report Routes */}
                    <Route
                      path="/admin/reports/fechamento-sintetico"
                      element={<ReportFechamentoSintetico />}
                    />
                    <Route
                      path="/admin/reports/fechamento-analitico"
                      element={<ReportRelatorioAnalitico />}
                    />
                    <Route
                      path="/admin/reports/cancelamento"
                      element={<ReportCancelamento />}
                    />
                    <Route
                      path="/admin/reports/escalas"
                      element={<ReportEscalas />}
                    />
                    <Route
                      path="/admin/reports/fechamento-dep-pessoal"
                      element={<ReportFechamentoDepPessoal />}
                    />

                    <Route path="/termos-de-uso" element={<TermosDeUso />} />
                  </Routes>
                </div>
              </NotificationsProvider>
            </ReportProvider>
          </AdminMiddleware>
        </TermsMiddleware>
      </AuthMiddleware>
    </SelectedDateProvider>
  );
}

export default App;
